import React from "react";
import { Button } from "components/common/Button";
import { Callout } from "components/common/Callout";
import { InlineSpinner } from "components/common/InlineSpinner";
import { plural } from "components/common/ImpactRow";
import { Inline } from "components/common/Stack";
import { formatShortDate } from "@/lib/dates";
import type { PendingSeatsView } from "@/lib/api/organizations";

export const DoubleBilledNotice: React.FC<{ subscriptionId: string }> = ({
  subscriptionId,
}) => (
  <Callout variant="error" title="Billed twice" className="mx-2">
    This organization has two live subscriptions, so it is being charged twice.
    Cancelling below ends <span className="font-mono">{subscriptionId}</span>{" "}
    only — contact support to have the other one refunded and closed.
  </Callout>
);

export const SeatShortfallNotice: React.FC<{
  shortfall: { members: number; seats: number };
  onAddSeats: (() => void) | null;
}> = ({ shortfall, onAddSeats }) => (
  <Callout
    variant="warning"
    title="Add a seat before inviting anyone new"
    className="mx-2"
  >
    <span
      className="flex items-start justify-between gap-3"
      data-testid="seat-shortfall"
    >
      <span className="min-w-0 flex-1">
        {`The subscription carries ${shortfall.seats} ${plural(shortfall.seats, "seat")} ` +
          `and this organization has ${shortfall.members} members. Everyone keeps ` +
          `working — invitations are what stop, including any already sent.`}
      </span>
      {onAddSeats && (
        <span className="shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={onAddSeats}
            data-testid="fix-seat-shortfall"
          >
            Add seats
          </Button>
        </span>
      )}
    </span>
  </Callout>
);

export const PendingSeatsNotice: React.FC<{
  pending: PendingSeatsView;
  heldSeats: number;
  onKeep: (() => void) | null;
}> = ({ pending, heldSeats, onKeep }) => (
  <Callout
    variant="info"
    title="Seats drop at the end of this cycle"
    className="mx-2"
  >
    <span
      className="flex items-center justify-between gap-3"
      data-testid="pending-seat-decrease"
    >
      <span>
        {`This organization renews at ${pending.seats.toLocaleString()} ${plural(pending.seats, "seat")}` +
          `${pending.effective_at ? ` on ${formatShortDate(new Date(pending.effective_at * 1000))}` : ""}. ` +
          `The ${heldSeats.toLocaleString()} you have now stay until then.`}
      </span>
      {onKeep && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onKeep}
          data-testid="keep-seats"
        >
          Keep {heldSeats.toLocaleString()}
        </Button>
      )}
    </span>
  </Callout>
);

export const NoCardNotice: React.FC<{
  cycleDate: string | null;
  onAddCard: () => void;
}> = ({ cycleDate, onAddCard }) => (
  <Callout variant="warning" title="No card on file" className="mx-2 mb-2">
    <span className="flex flex-col items-start gap-2">
      <span>
        {cycleDate
          ? `The next payment on ${cycleDate} will fail. Add a card for this organization.`
          : "The next payment will fail. Add a card for this organization."}
      </span>
      <Button
        variant="primary"
        size="sm"
        onClick={onAddCard}
        data-testid="no-card-update"
      >
        Add a card
      </Button>
    </span>
  </Callout>
);

export const PayerLeftNotice: React.FC<{
  billedTo: string | null;
  onUpdateCard: (() => void) | null;
  onEditDetails: () => void;
}> = ({ billedTo, onUpdateCard, onEditDetails }) => (
  <Callout
    variant="warning"
    title="The person paying for this organization has left"
    className="mx-2 mb-2"
  >
    <span className="flex flex-col items-start gap-2">
      <span>
        Their card is still the one being charged, and invoices still go to{" "}
        {billedTo}. Update the card, then the billing email.
      </span>
      {/* Card first: the email change alone quiets this callout. */}
      <Inline gap="sm">
        {onUpdateCard && (
          <Button
            variant="primary"
            size="sm"
            onClick={onUpdateCard}
            data-testid="payer-left-card"
          >
            Update card
          </Button>
        )}
        <Button
          variant={onUpdateCard ? "secondary" : "primary"}
          size="sm"
          onClick={onEditDetails}
          data-testid="payer-left-edit"
        >
          Edit billing details
        </Button>
      </Inline>
    </span>
  </Callout>
);

export const PaymentStuckNotice: React.FC = () => (
  <Callout
    variant="error"
    title="A payment needs attention"
    className="mx-2 mb-2"
  >
    A recent payment could not be applied to this organization. Nothing about
    your payment is lost — contact support to finish the setup.
  </Callout>
);

export const PaymentApplyingNotice: React.FC<{
  watchExpired: boolean;
  onRecheck: () => void;
}> = ({ watchExpired, onRecheck }) => (
  <Callout
    variant={watchExpired ? "warning" : "info"}
    title={watchExpired ? "Still setting up" : "Payment received"}
    className="mx-2 mb-2"
  >
    <span className="flex flex-col items-start gap-2">
      <span className="flex items-center gap-2">
        {!watchExpired && <InlineSpinner size={14} />}
        {watchExpired
          ? "Your new plan is taking longer than usual to activate. Nothing about your payment is lost."
          : "Setting up your new plan. This page updates the moment it lands."}
      </span>
      {watchExpired && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRecheck}
          data-testid="recheck-plan"
        >
          Check again
        </Button>
      )}
    </span>
  </Callout>
);
